import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFImage, type PDFPage } from 'pdf-lib'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { supabase } from './supabase'
import { fileDAttente } from './db'
import { ouvrirPlan } from './plans'
import { ouvrirPage } from './decoupage'
import { jourCalendrier } from './format'
import { codeSuivi, LIBELLE_STATUT, photosDuSuivi, TYPES } from './suivis'
import type { Chantier, DocumentChantier, Suivi } from './types'

/**
 * Fiche d'un suivi en PDF, pour l'envoyer à un contact externe (architecte,
 * entrepreneur) qui n'a pas de compte dans l'application. Faite sur
 * l'appareil : description, extrait du plan avec l'épingle, photos.
 * Aucun montant, aucune note interne (les commentaires ne sont pas inclus).
 */

const NOIR = rgb(0, 0, 0)
const GRIS = rgb(0.3, 0.3, 0.28)
const GRIS_PALE = rgb(0.87, 0.86, 0.83)
const OR = rgb(1, 0.792, 0)

const MARGE = 42
const LARGEUR = 612 // Lettre US, en points
const HAUTEUR = 792
const MAX_PHOTOS = 8

export interface ResultatPdf {
  blob: Blob
  nomFichier: string
  /** Éléments qui n'ont pas pu être inclus (hors ligne, plan introuvable…). */
  manques: string[]
}

export async function fichePdf(s: Suivi, chantier: Chantier, nomAuteur: string): Promise<ResultatPdf> {
  const manques: string[] = []
  const pdf = await PDFDocument.create()
  const police = await pdf.embedFont(StandardFonts.Helvetica)
  const gras = await pdf.embedFont(StandardFonts.HelveticaBold)
  const code = codeSuivi(s)
  const type = TYPES.find((t) => t.cle === s.type)!
  pdf.setTitle(`${code} — ${s.titre}`)
  pdf.setAuthor('Isolation Larente')
  pdf.setCreator('Chantiers — Isolation Larente')

  const ctx = new Mise(pdf, police, gras)
  ctx.nouvellePage()

  // En-tête : logo, puis bande or.
  const logo = await imageLogo(pdf)
  if (logo) {
    const h = 30
    const l = (logo.width / logo.height) * h
    ctx.page.drawImage(logo, { x: MARGE, y: ctx.y - h, width: l, height: h })
  } else {
    ctx.page.drawText('ISOLATION LARENTE', { x: MARGE, y: ctx.y - 20, size: 16, font: gras, color: NOIR })
  }
  ctx.page.drawText(propre(`${chantier.numero} · ${chantier.nom}`, police), {
    x: LARGEUR - MARGE - Math.min(260, police.widthOfTextAtSize(propre(`${chantier.numero} · ${chantier.nom}`, police), 9)),
    y: ctx.y - 12,
    size: 9,
    font: police,
    color: GRIS,
    maxWidth: 260
  })
  ctx.y -= 44
  ctx.page.drawRectangle({ x: MARGE, y: ctx.y, width: LARGEUR - 2 * MARGE, height: 3, color: OR })
  ctx.y -= 26

  // Titre
  ctx.texte(`${code} — ${type.singulier.charAt(0).toUpperCase() + type.singulier.slice(1)}`, { taille: 11, police: gras, couleur: GRIS })
  ctx.y -= 2
  ctx.texte(s.titre, { taille: 17, police: gras })
  ctx.y -= 8

  // Tableau des informations
  const lignes: [string, string][] = [
    ['Chantier', `${chantier.numero} — ${chantier.nom}`],
    ...(chantier.adresse ? ([['Adresse', chantier.adresse]] as [string, string][]) : []),
    ['Statut', LIBELLE_STATUT[s.statut] ?? s.statut],
    ...(s.assigne_contact_nom ? ([['Assignée à', s.assigne_contact_nom]] as [string, string][]) : []),
    ...(s.echeance ? ([['Échéance', jourCalendrier(s.echeance)]] as [string, string][]) : []),
    ...(s.emplacement ? ([['Emplacement', s.emplacement]] as [string, string][]) : []),
    ...(s.epingle_libelle ? ([['Plan', s.epingle_libelle]] as [string, string][]) : []),
    ['Émise le', jourCalendrier(s.cree_le.slice(0, 10))],
    ['Émise par', `${nomAuteur}, Isolation Larente`]
  ]
  for (const [l, v] of lignes) ctx.ligneInfo(l, v)
  ctx.y -= 10

  if (s.description) {
    ctx.section(s.type === 'qrt' ? 'Question' : 'Description')
    ctx.paragraphe(s.description)
  }
  if (s.type === 'qrt' && s.reponse) {
    ctx.section(`Réponse${s.repondue_le ? ' du ' + jourCalendrier(s.repondue_le) : ''}`)
    ctx.paragraphe(s.reponse)
  }

  // Extrait du plan, avec l'épingle
  if (s.epingle_document_id && s.epingle_page != null && s.epingle_x != null && s.epingle_y != null) {
    try {
      const extrait = await extraitPlan(s, code)
      const img = await pdf.embedJpg(extrait)
      const lMax = LARGEUR - 2 * MARGE
      const hImg = Math.min(300, (img.height / img.width) * lMax)
      ctx.section(`Emplacement sur le plan${s.epingle_libelle ? ' — ' + s.epingle_libelle : ''}`, hImg + 8)
      ctx.image(img, 300)
    } catch (e) {
      manques.push(`extrait du plan (${e instanceof Error ? e.message : String(e)})`)
    }
  }

  // Photos
  const liees = await photosDuSuivi(s.id)
  if (liees.length > 0) {
    const images: PDFImage[] = []
    for (const p of liees.slice(0, MAX_PHOTOS)) {
      try {
        const brut = await blobPhoto(p.photo_client_uuid)
        images.push(await pdf.embedJpg(await enJpeg(brut, 1400)))
      } catch {
        manques.push('une photo')
      }
    }
    if (liees.length > MAX_PHOTOS) manques.push(`${liees.length - MAX_PHOTOS} photo(s) au-delà de ${MAX_PHOTOS}`)
    if (images.length > 0) {
      ctx.section(`Photos (${images.length})`, ((LARGEUR - 2 * MARGE - 10) / 2) * 0.75 + 10)
      ctx.grillePhotos(images)
    }
  }

  ctx.piedsDePage(`${code} · ${chantier.numero}`)

  const octets = await pdf.save()
  const nomFichier = `${code} ${chantier.numero} ${s.titre}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\- ]+/g, '')
    .trim()
    .slice(0, 80)
    .replace(/ +/g, '_')
  return { blob: new Blob([octets as unknown as ArrayBuffer], { type: 'application/pdf' }), nomFichier: `${nomFichier}.pdf`, manques }
}

/* ---------------------------------------------------------------------
   Mise en page
   --------------------------------------------------------------------- */

class Mise {
  page!: PDFPage
  y = 0
  constructor(
    private pdf: PDFDocument,
    private police: PDFFont,
    private gras: PDFFont
  ) {}

  nouvellePage() {
    this.page = this.pdf.addPage([LARGEUR, HAUTEUR])
    this.y = HAUTEUR - MARGE
  }

  /** Passe à la page suivante s'il ne reste pas `h` points. */
  place(h: number) {
    if (this.y - h < MARGE + 24) this.nouvellePage()
  }

  texte(t: string, o: { taille?: number; police?: PDFFont; couleur?: ReturnType<typeof rgb>; x?: number; largeur?: number } = {}) {
    const taille = o.taille ?? 10.5
    const police = o.police ?? this.police
    const x = o.x ?? MARGE
    const largeur = o.largeur ?? LARGEUR - MARGE - x
    for (const l of couper(propre(t, police), police, taille, largeur)) {
      this.place(taille * 1.35)
      this.page.drawText(l, { x, y: this.y - taille, size: taille, font: police, color: o.couleur ?? NOIR })
      this.y -= taille * 1.35
    }
  }

  ligneInfo(libelle: string, valeur: string) {
    const taille = 10
    const xV = MARGE + 100
    const lignes = couper(propre(valeur, this.police), this.police, taille, LARGEUR - MARGE - xV)
    const h = lignes.length * taille * 1.35 + 6
    this.place(h)
    this.page.drawText(propre(libelle, this.police), { x: MARGE, y: this.y - taille - 3, size: taille, font: this.police, color: GRIS })
    let y = this.y - 3
    for (const l of lignes) {
      this.page.drawText(l, { x: xV, y: y - taille, size: taille, font: this.gras, color: NOIR })
      y -= taille * 1.35
    }
    this.y -= h
    this.page.drawLine({ start: { x: MARGE, y: this.y }, end: { x: LARGEUR - MARGE, y: this.y }, thickness: 0.5, color: GRIS_PALE })
  }

  /** Titre de section ; `suite` = hauteur du contenu qui doit rester sur la même page. */
  section(titre: string, suite = 40) {
    this.place(32 + suite)
    this.y -= 14
    this.page.drawText(propre(titre.toUpperCase(), this.gras), { x: MARGE, y: this.y - 9, size: 9, font: this.gras, color: GRIS })
    this.y -= 18
  }

  paragraphe(t: string) {
    for (const bloc of t.split(/\r?\n/)) {
      if (!bloc.trim()) {
        this.y -= 6
        continue
      }
      this.texte(bloc, { taille: 10.5 })
    }
  }

  image(img: PDFImage, hauteurMax: number) {
    const lMax = LARGEUR - 2 * MARGE
    let l = lMax
    let h = (img.height / img.width) * l
    if (h > hauteurMax) {
      h = hauteurMax
      l = (img.width / img.height) * h
    }
    this.place(h + 4)
    this.page.drawImage(img, { x: MARGE, y: this.y - h, width: l, height: h })
    this.page.drawRectangle({ x: MARGE, y: this.y - h, width: l, height: h, borderColor: GRIS_PALE, borderWidth: 0.75 })
    this.y -= h + 8
  }

  /** Deux photos par rangée. */
  grillePhotos(images: PDFImage[]) {
    const ecart = 10
    const l = (LARGEUR - 2 * MARGE - ecart) / 2
    const hCase = l * 0.75
    for (let i = 0; i < images.length; i += 2) {
      this.place(hCase + ecart)
      for (let j = 0; j < 2 && i + j < images.length; j++) {
        const img = images[i + j]
        const r = Math.min(l / img.width, hCase / img.height)
        const w = img.width * r
        const h = img.height * r
        const x = MARGE + j * (l + ecart) + (l - w) / 2
        this.page.drawImage(img, { x, y: this.y - hCase + (hCase - h) / 2, width: w, height: h })
      }
      this.y -= hCase + ecart
    }
  }

  piedsDePage(libelle: string) {
    const pages = this.pdf.getPages()
    pages.forEach((p, i) => {
      const t = propre(`${libelle} · page ${i + 1} de ${pages.length} · Isolation Larente`, this.police)
      p.drawText(t, { x: MARGE, y: 22, size: 8, font: this.police, color: GRIS })
    })
  }
}

/** Retire les caractères que la police standard ne sait pas écrire. */
function propre(t: string, police: PDFFont): string {
  const remplace = t
    .replace(/→/g, '->')
    .replace(/[‐-‒]/g, '-')
    .replace(/\t/g, '    ')
  let r = ''
  for (const c of remplace) {
    try {
      police.encodeText(c)
      r += c
    } catch {
      r += '?'
    }
  }
  return r
}

function couper(t: string, police: PDFFont, taille: number, largeur: number): string[] {
  const mots = t.split(/ +/)
  const lignes: string[] = []
  let cour = ''
  for (const m of mots) {
    const essai = cour ? `${cour} ${m}` : m
    if (police.widthOfTextAtSize(essai, taille) <= largeur) {
      cour = essai
      continue
    }
    if (cour) lignes.push(cour)
    // Mot plus long que la ligne : coupé à la lettre.
    let reste = m
    while (police.widthOfTextAtSize(reste, taille) > largeur && reste.length > 1) {
      let n = reste.length
      while (n > 1 && police.widthOfTextAtSize(reste.slice(0, n), taille) > largeur) n--
      lignes.push(reste.slice(0, n))
      reste = reste.slice(n)
    }
    cour = reste
  }
  if (cour) lignes.push(cour)
  return lignes.length ? lignes : ['']
}

/* ---------------------------------------------------------------------
   Images : logo, photos, extrait du plan
   --------------------------------------------------------------------- */

function chargerImage(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, ko) => {
    const img = new Image()
    img.onload = () => ok(img)
    img.onerror = () => ko(new Error('Image illisible.'))
    img.src = src
  })
}

function versBlob(c: HTMLCanvasElement, type: string, qualite?: number): Promise<Blob> {
  return new Promise((ok, ko) => c.toBlob((b) => (b ? ok(b) : ko(new Error('Conversion impossible.'))), type, qualite))
}

async function imageLogo(pdf: PDFDocument): Promise<PDFImage | null> {
  try {
    const img = await chargerImage('/logo-horizontal.svg')
    const l = 1156
    const h = Math.round((122 / 578) * l)
    const c = document.createElement('canvas')
    c.width = l
    c.height = h
    c.getContext('2d')!.drawImage(img, 0, 0, l, h)
    return await pdf.embedPng(await (await versBlob(c, 'image/png')).arrayBuffer())
  } catch {
    return null
  }
}

/** Photo d'un suivi : sur l'appareil si elle n'est pas encore envoyée, sinon sur le serveur. */
async function blobPhoto(clientUuid: string): Promise<Blob> {
  const locale = (await fileDAttente()).find((p) => p.client_uuid === clientUuid)
  if (locale) return locale.blob
  if (!navigator.onLine) throw new Error('hors ligne')
  const { data, error } = await supabase.from('photos').select('fichier_path').eq('client_uuid', clientUuid).single()
  if (error || !data) throw error ?? new Error('Photo introuvable.')
  const { data: f, error: e2 } = await supabase.storage.from('photos').download((data as { fichier_path: string }).fichier_path)
  if (e2 || !f) throw e2 ?? new Error('Photo introuvable.')
  return f
}

async function enJpeg(brut: Blob, cote: number): Promise<ArrayBuffer> {
  const url = URL.createObjectURL(brut)
  try {
    const img = await chargerImage(url)
    const r = Math.min(1, cote / Math.max(img.naturalWidth, img.naturalHeight))
    const c = document.createElement('canvas')
    c.width = Math.round(img.naturalWidth * r)
    c.height = Math.round(img.naturalHeight * r)
    const g = c.getContext('2d')!
    g.fillStyle = '#fff'
    g.fillRect(0, 0, c.width, c.height)
    g.drawImage(img, 0, 0, c.width, c.height)
    return await (await versBlob(c, 'image/jpeg', 0.82)).arrayBuffer()
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Rend la page épinglée et découpe une zone autour de l'épingle (environ le
 * tiers de la largeur de la feuille), avec l'épingle dessinée par-dessus.
 */
async function extraitPlan(s: Suivi, code: string): Promise<ArrayBuffer> {
  const { data, error } = navigator.onLine
    ? await supabase
        .from('documents')
        .select('id, chantier_id, fichier_path, pages_decoupees, nb_pages')
        .eq('id', s.epingle_document_id!)
        .single()
    : { data: null, error: new Error('hors ligne') }
  if (error || !data) throw error ?? new Error('plan introuvable')
  const doc = data as DocumentChantier
  const decoupe = !!doc.pages_decoupees || doc.fichier_path.includes('/pages/')
  const pdf: PDFDocumentProxy = decoupe ? await ouvrirPage(doc, s.epingle_page!) : await ouvrirPlan(doc)
  try {
    const page = await pdf.getPage(decoupe ? 1 : s.epingle_page!)
    const base = page.getViewport({ scale: 1 })
    const echelle = 3600 / Math.max(base.width, base.height)
    const vue = page.getViewport({ scale: echelle })

    // Zone visée : 34 % de la largeur, rapport 16:10, centrée sur l'épingle.
    const lZone = Math.round(vue.width * 0.34)
    const hZone = Math.round(lZone * 0.625)
    const px = s.epingle_x! * vue.width
    const py = s.epingle_y! * vue.height
    const x0 = Math.max(0, Math.min(vue.width - lZone, px - lZone / 2))
    const y0 = Math.max(0, Math.min(vue.height - hZone, py - hZone / 2))

    const c = document.createElement('canvas')
    c.width = lZone
    c.height = hZone
    const g = c.getContext('2d')!
    g.fillStyle = '#fff'
    g.fillRect(0, 0, lZone, hZone)
    await page.render({
      canvasContext: g,
      viewport: vue,
      transform: [1, 0, 0, 1, -x0, -y0]
    }).promise

    // Épingle : cercle or cerclé de noir, et étiquette du code.
    const ex = px - x0
    const ey = py - y0
    const r = Math.max(14, lZone * 0.018)
    g.lineWidth = r * 0.35
    g.strokeStyle = '#000'
    g.fillStyle = 'rgba(255, 202, 0, 0.35)'
    g.beginPath()
    g.arc(ex, ey, r * 2.4, 0, Math.PI * 2)
    g.fill()
    g.stroke()
    g.fillStyle = '#FFCA00'
    g.beginPath()
    g.arc(ex, ey, r * 0.6, 0, Math.PI * 2)
    g.fill()
    g.stroke()
    const taille = Math.round(r * 1.5)
    g.font = `bold ${taille}px Helvetica, Arial, sans-serif`
    const lt = g.measureText(code).width + taille
    const tx = Math.min(lZone - lt - 4, ex + r * 2.8)
    const ty = Math.max(4, ey - r * 2.8 - taille * 1.4)
    g.fillStyle = '#000'
    g.fillRect(tx, ty, lt, taille * 1.5)
    g.fillStyle = '#FFCA00'
    g.fillText(code, tx + taille / 2, ty + taille * 1.1)

    return await (await versBlob(c, 'image/jpeg', 0.85)).arrayBuffer()
  } finally {
    void pdf.destroy()
  }
}
